import React, { useState, useEffect } from 'react';
import { sessionsAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { 
  Monitor, 
  Smartphone, 
  Tablet,
  Loader2, 
  LogOut,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';

function ActiveSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const { success, error: showError } = useToast();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await sessionsAPI.getAll();
      setSessions(response.data);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      // Don't show error for initial load failure - table might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevoking(sessionId);
    try {
      await sessionsAPI.revoke(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      success('Session revoked successfully');
    } catch (err) {
      console.error('Failed to revoke session:', err);
      showError('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOthers = async () => {
    setRevokingAll(true);
    try {
      const response = await sessionsAPI.revokeOthers();
      success(response.data.message);
      // Reload sessions to show only current
      await loadSessions();
    } catch (err) {
      console.error('Failed to revoke other sessions:', err);
      showError('Failed to revoke other sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  const getDeviceIcon = (deviceInfo) => {
    const device = deviceInfo?.device?.toLowerCase() || 'desktop';
    if (device.includes('mobile') || device.includes('phone')) {
      return <Smartphone size={20} />;
    }
    if (device.includes('tablet')) {
      return <Tablet size={20} />;
    }
    return <Monitor size={20} />;
  };

  const getDeviceName = (deviceInfo) => {
    if (!deviceInfo) return 'Unknown Device';
    
    const parts = [];
    if (deviceInfo.browser && deviceInfo.browser !== 'Unknown') {
      parts.push(deviceInfo.browser);
    }
    if (deviceInfo.os && deviceInfo.os !== 'Unknown') {
      parts.push(`on ${deviceInfo.os}`);
    }
    
    return parts.length > 0 ? parts.join(' ') : 'Unknown Device';
  };

  const formatLastActive = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const otherSessions = sessions.filter(s => !s.is_current);

  if (loading) {
    return (
      <div className="settings-group">
        <h3>Active Sessions</h3>
        <div className="loading-state">
          <Loader2 size={24} className="spinner" />
          <span>Loading sessions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-group">
      <h3>Active Sessions</h3>
      <p className="setting-description">Manage devices where you're currently logged in</p>
      
      <div className="session-list">
        {sessions.length === 0 ? (
          <div className="session-item current">
            <div className="session-info">
              <div className="session-icon">
                <Monitor size={20} />
              </div>
              <div className="session-details">
                <h4>Current Session</h4>
                <div className="session-meta">
                  <span className="session-time">
                    <Clock size={12} />
                    Active now
                  </span>
                </div>
              </div>
            </div>
            <span className="session-badge current">
              <CheckCircle size={14} />
              This device
            </span>
          </div>
        ) : (
          <>
            {/* Current Session */}
            {sessions.filter(s => s.is_current).map(session => (
              <div key={session.id} className="session-item current">
                <div className="session-info">
                  <div className="session-icon">
                    {getDeviceIcon(session.device_info)}
                  </div>
                  <div className="session-details">
                    <h4>{getDeviceName(session.device_info)}</h4>
                    <div className="session-meta">
                      <span className="session-ip">{session.ip_address || 'Unknown IP'}</span>
                      <span className="session-time">
                        <Clock size={12} />
                        Active now
                      </span>
                    </div>
                  </div>
                </div>
                <span className="session-badge current">
                  <CheckCircle size={14} />
                  This device
                </span>
              </div>
            ))}

            {/* Other Sessions */}
            {otherSessions.map(session => (
              <div key={session.id} className="session-item">
                <div className="session-info">
                  <div className="session-icon">
                    {getDeviceIcon(session.device_info)}
                  </div>
                  <div className="session-details">
                    <h4>{getDeviceName(session.device_info)}</h4>
                    <div className="session-meta">
                      <span className="session-ip">{session.ip_address || 'Unknown IP'}</span>
                      <span className="session-time">
                        <Clock size={12} />
                        {formatLastActive(session.last_active)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  className="btn-danger-outline btn-sm"
                  onClick={() => handleRevokeSession(session.id)}
                  disabled={revoking === session.id}
                  title="Revoke this session"
                >
                  {revoking === session.id ? (
                    <Loader2 size={14} className="spinner" />
                  ) : (
                    <><LogOut size={14} /> Revoke</>
                  )}
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Revoke All Others Button */}
      {otherSessions.length > 0 && (
        <div className="session-actions">
          <button
            className="btn-danger-outline"
            onClick={handleRevokeAllOthers}
            disabled={revokingAll}
          >
            {revokingAll ? (
              <><Loader2 size={16} className="spinner" /> Revoking...</>
            ) : (
              <><LogOut size={16} /> Log out of all other sessions</>
            )}
          </button>
          <p className="session-warning">
            <AlertTriangle size={14} />
            This will log you out of {otherSessions.length} other device{otherSessions.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}

export default ActiveSessions;
